import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, TextField, Button, Accordion, AccordionSummary,
  AccordionDetails, IconButton, Alert, Snackbar, CircularProgress, Divider,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SaveIcon from '@mui/icons-material/Save';
import WebIcon from '@mui/icons-material/Web';
import UploadIcon from '@mui/icons-material/Upload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { adminFetch } from '../../lib/adminAuth';
import { isWithinUploadLimit, tooLargeMessage } from '../../lib/uploadLimits';

const API = process.env.REACT_APP_API_URL || '';

/* ── Default values matching the current hardcoded LandingPage.js ── */
const DEFAULTS = {
  hero: {
    heading: 'Customer loyalty, powered by ',
    headingHighlight: 'marketing science',
    subheading: "The modern marketing platform based on customer's behavior that helps businesses build lasting relationships, increase sales, digital stamp cards, smart notifications, and powerful analytics — all in one place.",
    cta1Text: 'Register Now',
    cta2Text: 'See How It Works',
    image: '',
    imagePosition: 'right',
  },
  marquee: {
    title: 'Built for every local business',
    businessTypes: ['Caf\u00e9s', 'Restaurants', 'Hair Salons', 'Nail Salons', 'Gyms', 'Car Washes', 'Pet Groomers', 'Massage Studios', 'Beauty Bars', 'Bakeries', 'Juice Bars', 'Barbershops'],
  },
  features: {
    badge: 'Powerful Features',
    title: 'Everything you need to build ',
    titleHighlight: 'customer loyalty',
    subtitle: 'From digital stamp cards to automated marketing \u2014 Rewarding gives you all the tools to keep customers coming back.',
    items: [
      { title: 'Digital Stamp Cards', description: 'Replace paper punch cards with beautiful digital stamp cards. Customers scan a QR code \u2014 simple, fast, and eco-friendly.' },
      { title: 'Smart Notifications', description: 'AI Automated and personalized messages for birthdays, visit reminders, and loyalty milestones. Keep customers engaged without lifting a finger.' },
      { title: 'Real-Time Analytics', description: 'Track scans, redemptions, and customer retention with a powerful dashboard. Make data-driven decisions that grow your business.' },
      { title: 'Collaborate with Businesses', description: 'Work with other businesses around you to expand your reach and increase sales.' },
      { title: 'Customer Insights', description: 'Understand your customers better. See who visits most, track repeat rates, and identify your most loyal patrons.' },
      { title: 'Targeted Campaigns', description: 'Launch promotions that reach the right audience. Target by visit frequency, location, and customer behavior.' },
    ],
  },
  personalisation: {
    badge: 'Personalised by AI',
    title: 'Every customer is different. ',
    titleHighlight: 'Every message should be too.',
    body: [
      "Rewarding learns what makes your business unique and what each customer likes, then automatically creates personalised messages that feel like they're coming directly from you.",
      'From birthday wishes and special offers to reminders and thank-you messages, every message is tailored to strengthen relationships and encourage customers to return.',
    ],
    tagline: 'The right message. The right customer. The right time.',
  },
  howItWorks: {
    badge: 'Simple Setup',
    title: 'No technical skills ',
    titleHighlight: 'needed',
    subtitle: 'Get your account live in four simple steps.',
    steps: [
      { title: 'Sign Up in Minutes', description: 'Create your account, set up your business profile, and customise your brand \u2014 all in under 5 minutes.' },
      { title: 'Design Your Rewards', description: 'Create digital stamp cards with your logo, colours, and reward tiers. Choose how many stamps earn a reward.' },
      { title: 'Share Your QR Code', description: 'Print your unique QR code or display it digitally. Customers scan it with their phone \u2014 no app download needed.' },
      { title: 'Watch Sales Grow', description: 'Track customer visits, see real-time analytics, and watch your repeat business increase week over week.' },
    ],
  },
  dashboard: {
    badge: 'Analytics Dashboard',
    title: 'Know your customers ',
    titleHighlight: 'inside out',
    subtitle: 'Track every scan, redemption, and customer interaction in real-time. Our intuitive dashboard gives you the insights you need to grow.',
    bulletPoints: [
      'Weekly scan trends and customer visit patterns',
      'Customer retention and repeat visit rates',
      'Automated birthday and loyalty notifications',
      'Campaign performance and ROI tracking',
    ],
    image: '',
    imagePosition: 'right',
  },
  testimonials: {
    badge: 'Customer Love',
    title: 'Loved by businesses ',
    titleHighlight: 'everywhere',
    subtitle: 'See what business owners are saying about Rewarding.',
    items: [
      { name: 'Sarah M.', role: 'Caf\u00e9 Owner', stars: 5, quote: 'Rewarding transformed our customer retention. We saw a 40% increase in repeat visits within the first month. The digital stamp cards are a game-changer.' },
      { name: 'James L.', role: 'Barber Shop', stars: 5, quote: "Our clients love the simplicity. No more lost paper cards \u2014 just a quick scan and they're on their way. The birthday notifications are a brilliant touch." },
      { name: 'Priya K.', role: 'Nail Salon', stars: 5, quote: 'The analytics dashboard gives us insights we never had before. We can see exactly which promotions work and adjust our strategy in real-time.' },
      { name: 'Tom W.', role: 'Pizza Restaurant', stars: 5, quote: 'Setting up was incredibly easy. Within 10 minutes we had our QR code printed and customers were scanning. The automated reminders bring people back.' },
    ],
  },
  pricing: {
    badge: 'Simple Pricing',
    title: '',
    titleHighlight: 'Everything You Need',
    subtitle: 'Turn one-time visitors into loyal customers with rewards, automated AI smart messages, and local collaborations.',
    price: '$49.99',
    priceSubtext: 'No lock-in contract \u2022 Cancel anytime',
    ctaText: 'Get Started Now',
    features: [
      { title: 'Smart Rewards Program', description: 'Launch your own digital rewards system in minutes. Keep customers coming back automatically.', bullets: [], footer: '' },
      { title: 'Customer Data & Insights', description: '', bullets: ['Who visits most', "Who hasn't returned", 'Buying patterns & trends'], footer: '' },
      { title: 'AI "We Miss You" Reminders', description: "Automatically send personalised messages to customers who haven't visited in a while. Bring them back \u2014 without lifting a finger.", bullets: [], footer: '' },
      { title: 'Automated Birthday Messages', description: '', bullets: ['A birthday greeting OR', 'A birthday offer to drive visits'], footer: 'Set it once. It runs forever.' },
      { title: 'Monthly Thank You / Offer Message', description: 'Send one free campaign per month to all your customers. Perfect for:', bullets: ['Promotions', 'New arrivals', 'Seasonal offers', 'Slow periods'], footer: '' },
      { title: 'Business Collaboration Feature', description: '', bullets: ['Partner with nearby businesses.', 'When customers earn a reward at your store, they receive an exclusive offer from another local business \u2014 and vice versa.', 'Increase exposure. Increase sales. Together.'], footer: '' },
    ],
    replacesTitle: 'Rewarding replaces:',
    replacesItems: ['SMS marketing software', 'Loyalty card systems', 'Email marketing tools', 'CRM software', 'Paid ads for retention'],
  },
  cta: {
    heading: 'Ready to grow your ',
    headingHighlight: 'loyal customer base?',
    subheading: 'Join hundreds of businesses already using Rewarding to increase customer retention and drive repeat visits.',
    cta1Text: 'Register Now',
    cta2Text: 'Sign In',
  },
  footer: {
    description: 'The modern loyalty platform for local businesses. Build lasting customer relationships with digital stamp cards and smart automation.',
    companyLinks: ['Blog', 'Contact'],
    supportLinks: ['Privacy Policy', 'Terms of Service', 'support@rewarding.com'],
  },
  legal: {
    termsPdf: '',
    privacyPdf: '',
  },
};

/* Deep merge: server values override defaults, but missing keys fall back */
function deepMerge(defaults, overrides) {
  if (!overrides || typeof overrides !== 'object') return defaults;
  const result = { ...defaults };
  for (const key of Object.keys(defaults)) {
    if (overrides[key] === undefined || overrides[key] === null) continue;
    if (Array.isArray(defaults[key])) {
      result[key] = overrides[key];
    } else if (typeof defaults[key] === 'object' && !Array.isArray(defaults[key])) {
      result[key] = deepMerge(defaults[key], overrides[key]);
    } else {
      result[key] = overrides[key];
    }
  }
  return result;
}

/* ── Reusable sub-components ── */

const Field = ({ label, value, onChange, multiline, rows }) => (
  <TextField
    label={label}
    value={value || ''}
    onChange={e => onChange(e.target.value)}
    fullWidth
    size="small"
    multiline={multiline}
    rows={rows || 1}
    sx={{ mb: 2 }}
  />
);

const ArrayStringEditor = ({ label, items, onChange }) => {
  const update = (i, val) => { const a = [...items]; a[i] = val; onChange(a); };
  const remove = (i) => { const a = [...items]; a.splice(i, 1); onChange(a); };
  const add = () => onChange([...items, '']);
  const move = (i, dir) => { const a = [...items]; [a[i], a[i + dir]] = [a[i + dir], a[i]]; onChange(a); };
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, color: '#666' }}>{label}</Typography>
      {items.map((item, i) => (
        <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
          <TextField value={item} onChange={e => update(i, e.target.value)} size="small" fullWidth />
          <IconButton size="small" onClick={() => move(i, -1)} disabled={i === 0}><ArrowUpwardIcon fontSize="small" /></IconButton>
          <IconButton size="small" onClick={() => move(i, 1)} disabled={i === items.length - 1}><ArrowDownwardIcon fontSize="small" /></IconButton>
          <IconButton size="small" onClick={() => remove(i)} color="error"><DeleteIcon fontSize="small" /></IconButton>
        </Box>
      ))}
      <Button size="small" startIcon={<AddIcon />} onClick={add}>Add</Button>
    </Box>
  );
};

const ItemArrayEditor = ({ label, items, onChange, fields }) => {
  const update = (i, field, val) => { const a = [...items]; a[i] = { ...a[i], [field]: val }; onChange(a); };
  const remove = (i) => { const a = [...items]; a.splice(i, 1); onChange(a); };
  const add = () => {
    const empty = {};
    fields.forEach(f => { empty[f.key] = f.type === 'array' ? [] : (f.type === 'number' ? 5 : ''); });
    onChange([...items, empty]);
  };
  const move = (i, dir) => { const a = [...items]; [a[i], a[i + dir]] = [a[i + dir], a[i]]; onChange(a); };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, color: '#666' }}>{label}</Typography>
      {items.map((item, i) => (
        <Box key={i} sx={{ p: 2, mb: 1.5, border: '1px solid #e8e8e8', borderRadius: 2, bgcolor: '#fafafa' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption" sx={{ color: '#999' }}>#{i + 1}</Typography>
            <Box>
              <IconButton size="small" onClick={() => move(i, -1)} disabled={i === 0}><ArrowUpwardIcon fontSize="small" /></IconButton>
              <IconButton size="small" onClick={() => move(i, 1)} disabled={i === items.length - 1}><ArrowDownwardIcon fontSize="small" /></IconButton>
              <IconButton size="small" onClick={() => remove(i)} color="error"><DeleteIcon fontSize="small" /></IconButton>
            </Box>
          </Box>
          {fields.map(f => {
            if (f.type === 'array') {
              return (
                <ArrayStringEditor
                  key={f.key}
                  label={f.label}
                  items={item[f.key] || []}
                  onChange={val => update(i, f.key, val)}
                />
              );
            }
            if (f.type === 'number') {
              return (
                <TextField
                  key={f.key}
                  label={f.label}
                  type="number"
                  value={item[f.key] || 0}
                  onChange={e => update(i, f.key, parseInt(e.target.value) || 0)}
                  size="small"
                  fullWidth
                  sx={{ mb: 1.5 }}
                />
              );
            }
            return (
              <TextField
                key={f.key}
                label={f.label}
                value={item[f.key] || ''}
                onChange={e => update(i, f.key, e.target.value)}
                size="small"
                fullWidth
                multiline={f.multiline}
                rows={f.rows || 1}
                sx={{ mb: 1.5 }}
              />
            );
          })}
        </Box>
      ))}
      <Button size="small" startIcon={<AddIcon />} onClick={add}>Add Item</Button>
    </Box>
  );
};

const ImageUploader = ({ label, imageUrl, position, onImageChange, onPositionChange, onError }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isWithinUploadLimit(file)) {
      onError && onError(tooLargeMessage());
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await adminFetch(`${API}/api/admin/landing-content/upload-image`, { method: 'POST', body: fd });
      if (res.status === 413) throw new Error('That file is too large for the server to accept.');
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Image upload failed');
      }
      const data = await res.json();
      onImageChange(data.url);
    } catch (err) {
      onError && onError(err.message || 'Image upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <Box sx={{ mb: 2, p: 2, border: '1px solid #e8e8e8', borderRadius: 2, bgcolor: '#fafafa' }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#666' }}>{label}</Typography>
      {imageUrl ? (
        <Box sx={{ mb: 1.5 }}>
          <Box component="img" src={imageUrl} alt="" sx={{ maxWidth: '100%', maxHeight: 200, borderRadius: 1, border: '1px solid #e0e0e0', display: 'block', mb: 1 }} />
          <Typography variant="caption" sx={{ color: '#999', wordBreak: 'break-all' }}>{imageUrl}</Typography>
        </Box>
      ) : (
        <Typography variant="caption" sx={{ color: '#bbb', display: 'block', mb: 1.5 }}>No image — falling back to default preview.</Typography>
      )}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} style={{ display: 'none' }} />
        <Button size="small" variant="outlined" startIcon={uploading ? <CircularProgress size={14} /> : <UploadIcon />} onClick={() => inputRef.current?.click()} disabled={uploading} sx={{ textTransform: 'none' }}>
          {uploading ? 'Uploading...' : (imageUrl ? 'Replace' : 'Upload Image')}
        </Button>
        {imageUrl && (
          <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => onImageChange('')} sx={{ textTransform: 'none' }}>
            Remove
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        {/* Only shown for images that sit beside text. A full-width image has
            no side to be on, and without a handler this would throw on click. */}
        {onPositionChange && (
          <>
            <Typography variant="caption" sx={{ color: '#666', mr: 1 }}>Position:</Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={position || 'right'}
              onChange={(_, v) => v && onPositionChange(v)}
            >
              <ToggleButton value="left" sx={{ textTransform: 'none', px: 1.5 }}>Left</ToggleButton>
              <ToggleButton value="right" sx={{ textTransform: 'none', px: 1.5 }}>Right</ToggleButton>
            </ToggleButtonGroup>
          </>
        )}
      </Box>
    </Box>
  );
};

const PdfUploader = ({ label, fileUrl, onChange, onError }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      onError && onError('Please choose a PDF file');
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await adminFetch(`${API}/api/admin/landing-content/upload-pdf`, { method: 'POST', body: fd });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'PDF upload failed');
      }
      const data = await res.json();
      onChange(data.url);
    } catch (err) {
      onError && onError(err.message || 'PDF upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <Box sx={{ mb: 2, p: 2, border: '1px solid #e8e8e8', borderRadius: 2, bgcolor: '#fafafa' }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#666' }}>{label}</Typography>
      {fileUrl ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <PictureAsPdfIcon sx={{ color: '#d32f2f' }} />
          <Typography
            component="a"
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="caption"
            sx={{ color: '#1976d2', wordBreak: 'break-all', display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none' }}
          >
            View current PDF <OpenInNewIcon sx={{ fontSize: 13 }} />
          </Typography>
        </Box>
      ) : (
        <Typography variant="caption" sx={{ color: '#bbb', display: 'block', mb: 1.5 }}>No PDF uploaded — the footer link will be hidden.</Typography>
      )}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <input ref={inputRef} type="file" accept="application/pdf" onChange={handleFile} style={{ display: 'none' }} />
        <Button size="small" variant="outlined" startIcon={uploading ? <CircularProgress size={14} /> : <UploadIcon />} onClick={() => inputRef.current?.click()} disabled={uploading} sx={{ textTransform: 'none' }}>
          {uploading ? 'Uploading...' : (fileUrl ? 'Replace PDF' : 'Upload PDF')}
        </Button>
        {fileUrl && (
          <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => onChange('')} sx={{ textTransform: 'none' }}>
            Remove
          </Button>
        )}
      </Box>
    </Box>
  );
};

/* ════════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ════════════════════════════════════════════════════════════════════════════ */

export default function AdminLandingPage() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  const load = useCallback(async () => {
    try {
      const res = await adminFetch(`${API}/api/admin/landing-content`);
      const data = await res.json();
      setContent(deepMerge(DEFAULTS, data));
    } catch {
      setContent({ ...DEFAULTS });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await adminFetch(`${API}/api/admin/landing-content`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(content),
      });
      if (!res.ok) throw new Error();
      setSnack({ open: true, msg: 'Landing page saved!', severity: 'success' });
    } catch {
      setSnack({ open: true, msg: 'Failed to save', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const set = (section, field, value) => {
    setContent(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
  };

  if (loading) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;
  if (!content) return null;

  const c = content;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <WebIcon sx={{ color: '#999' }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a2e' }}>Landing Page</Typography>
          </Box>
          <Typography variant="body2" sx={{ color: '#999' }}>Edit all visible text on the public landing page</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />} onClick={save} disabled={saving} size="small"
            sx={{ textTransform: 'none', bgcolor: '#1a1a2e', color: '#fff', '&:hover': { bgcolor: '#2a2a4e' } }}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      {/* ── HERO ── */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 600 }}>Hero Section</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Field label="Heading" value={c.hero.heading} onChange={v => set('hero', 'heading', v)} />
          <Field label="Heading Highlight (colored text)" value={c.hero.headingHighlight} onChange={v => set('hero', 'headingHighlight', v)} />
          <Field label="Subheading" value={c.hero.subheading} onChange={v => set('hero', 'subheading', v)} multiline rows={3} />
          <Field label="Primary CTA Button" value={c.hero.cta1Text} onChange={v => set('hero', 'cta1Text', v)} />
          <Field label="Secondary CTA Button" value={c.hero.cta2Text} onChange={v => set('hero', 'cta2Text', v)} />
          <ImageUploader
            label="Hero Image"
            imageUrl={c.hero.image}
            position={c.hero.imagePosition}
            onImageChange={v => set('hero', 'image', v)}
            onPositionChange={v => set('hero', 'imagePosition', v)}
            onError={msg => setSnack({ open: true, msg, severity: 'error' })}
          />
        </AccordionDetails>
      </Accordion>

      {/* ── MARQUEE ── */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 600 }}>Business Types Marquee</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Field label="Title" value={c.marquee.title} onChange={v => set('marquee', 'title', v)} />
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            The business types shown in this marquee are now managed under{' '}
            <strong>Landing Page → Business Categories</strong>. Edit the list there and it
            updates here, in the registration form and in the vendor Store Settings.
          </Alert>
        </AccordionDetails>
      </Accordion>

      {/* ── FEATURES ── */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 600 }}>Features Section</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Field label="Badge Text" value={c.features.badge} onChange={v => set('features', 'badge', v)} />
          <Field label="Title" value={c.features.title} onChange={v => set('features', 'title', v)} />
          <Field label="Title Highlight" value={c.features.titleHighlight} onChange={v => set('features', 'titleHighlight', v)} />
          <Field label="Subtitle" value={c.features.subtitle} onChange={v => set('features', 'subtitle', v)} multiline rows={2} />
          <Divider sx={{ my: 2 }} />
          <ImageUploader
            label="Product Preview Image"
            imageUrl={c.features.image}
            onImageChange={v => set('features', 'image', v)}
            onError={msg => setSnack({ open: true, msg, severity: 'error' })}
          />
          <Divider sx={{ my: 2 }} />
          <ItemArrayEditor
            label="Feature Cards"
            items={c.features.items}
            onChange={v => set('features', 'items', v)}
            fields={[
              { key: 'title', label: 'Title' },
              { key: 'description', label: 'Description', multiline: true, rows: 2 },
            ]}
          />
        </AccordionDetails>
      </Accordion>

      {/* ── PERSONALISATION ── */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 600 }}>Personalisation</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Field label="Badge Text" value={c.personalisation.badge} onChange={v => set('personalisation', 'badge', v)} />
          <Field label="Title" value={c.personalisation.title} onChange={v => set('personalisation', 'title', v)} />
          <Field label="Title Highlight" value={c.personalisation.titleHighlight} onChange={v => set('personalisation', 'titleHighlight', v)} />
          <Divider sx={{ my: 2 }} />
          <ArrayStringEditor
            label="Paragraphs"
            items={c.personalisation.body}
            onChange={v => set('personalisation', 'body', v)}
          />
          <Divider sx={{ my: 2 }} />
          <Field label="Closing Tagline" value={c.personalisation.tagline} onChange={v => set('personalisation', 'tagline', v)} />
        </AccordionDetails>
      </Accordion>

      {/* ── HOW IT WORKS ── */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 600 }}>How It Works</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Field label="Badge Text" value={c.howItWorks.badge} onChange={v => set('howItWorks', 'badge', v)} />
          <Field label="Title" value={c.howItWorks.title} onChange={v => set('howItWorks', 'title', v)} />
          <Field label="Title Highlight" value={c.howItWorks.titleHighlight} onChange={v => set('howItWorks', 'titleHighlight', v)} />
          <Field label="Subtitle" value={c.howItWorks.subtitle} onChange={v => set('howItWorks', 'subtitle', v)} multiline rows={2} />
          <Divider sx={{ my: 2 }} />
          <ItemArrayEditor
            label="Steps"
            items={c.howItWorks.steps}
            onChange={v => set('howItWorks', 'steps', v)}
            fields={[
              { key: 'title', label: 'Step Title' },
              { key: 'description', label: 'Step Description', multiline: true, rows: 2 },
            ]}
          />
        </AccordionDetails>
      </Accordion>

      {/* ── TESTIMONIALS ── */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 600 }}>Testimonials</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Field label="Badge Text" value={c.testimonials.badge} onChange={v => set('testimonials', 'badge', v)} />
          <Field label="Title" value={c.testimonials.title} onChange={v => set('testimonials', 'title', v)} />
          <Field label="Title Highlight" value={c.testimonials.titleHighlight} onChange={v => set('testimonials', 'titleHighlight', v)} />
          <Field label="Subtitle" value={c.testimonials.subtitle} onChange={v => set('testimonials', 'subtitle', v)} multiline rows={2} />
          <Divider sx={{ my: 2 }} />
          <ItemArrayEditor
            label="Testimonial Cards"
            items={c.testimonials.items}
            onChange={v => set('testimonials', 'items', v)}
            fields={[
              { key: 'name', label: 'Name' },
              { key: 'role', label: 'Role / Business Type' },
              { key: 'stars', label: 'Stars (1-5)', type: 'number' },
              { key: 'quote', label: 'Quote', multiline: true, rows: 3 },
            ]}
          />
        </AccordionDetails>
      </Accordion>

      {/* ── PRICING ── */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 600 }}>Pricing</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Field label="Badge Text" value={c.pricing.badge} onChange={v => set('pricing', 'badge', v)} />
          <Field label="Title" value={c.pricing.title} onChange={v => set('pricing', 'title', v)} />
          <Field label="Title Highlight" value={c.pricing.titleHighlight} onChange={v => set('pricing', 'titleHighlight', v)} />
          <Field label="Subtitle" value={c.pricing.subtitle} onChange={v => set('pricing', 'subtitle', v)} multiline rows={2} />
          <Field label="Price (e.g. $49.99)" value={c.pricing.price} onChange={v => set('pricing', 'price', v)} />
          <Field label="Price Subtext" value={c.pricing.priceSubtext} onChange={v => set('pricing', 'priceSubtext', v)} />
          <Field label="CTA Button Text" value={c.pricing.ctaText} onChange={v => set('pricing', 'ctaText', v)} />
          <Divider sx={{ my: 2 }} />
          <ItemArrayEditor
            label="Pricing Feature Cards"
            items={c.pricing.features}
            onChange={v => set('pricing', 'features', v)}
            fields={[
              { key: 'title', label: 'Title' },
              { key: 'description', label: 'Description', multiline: true, rows: 2 },
              { key: 'bullets', label: 'Bullet Points', type: 'array' },
              { key: 'footer', label: 'Footer Text' },
            ]}
          />
          <Divider sx={{ my: 2 }} />
          <Field label="Replaces Title" value={c.pricing.replacesTitle} onChange={v => set('pricing', 'replacesTitle', v)} />
          <ArrayStringEditor label="Replaces Items" items={c.pricing.replacesItems} onChange={v => set('pricing', 'replacesItems', v)} />
          <Divider sx={{ my: 2 }} />
        </AccordionDetails>
      </Accordion>

      {/* ── FOOTER ── */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 600 }}>Footer</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Field label="Brand Description" value={c.footer.description} onChange={v => set('footer', 'description', v)} multiline rows={3} />
          <ArrayStringEditor label="Company Links" items={c.footer.companyLinks} onChange={v => set('footer', 'companyLinks', v)} />
          <ArrayStringEditor label="Support Links" items={c.footer.supportLinks} onChange={v => set('footer', 'supportLinks', v)} />
        </AccordionDetails>
      </Accordion>

      {/* ── LEGAL DOCUMENTS ── */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 600 }}>Legal Documents</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ color: '#999', mb: 2 }}>
            Upload PDFs for your Terms &amp; Conditions and Privacy Policy. They appear on the public <strong>/terms</strong> and <strong>/privacy</strong> pages and are linked from the footer. Remember to click Save Changes after uploading.
          </Typography>
          <PdfUploader
            label="Terms & Conditions (PDF)"
            fileUrl={c.legal.termsPdf}
            onChange={v => set('legal', 'termsPdf', v)}
            onError={msg => setSnack({ open: true, msg, severity: 'error' })}
          />
          <PdfUploader
            label="Privacy Policy (PDF)"
            fileUrl={c.legal.privacyPdf}
            onChange={v => set('legal', 'privacyPdf', v)}
            onError={msg => setSnack({ open: true, msg, severity: 'error' })}
          />
        </AccordionDetails>
      </Accordion>

      <Box sx={{ mt: 3, mb: 6, textAlign: 'right' }}>
        <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />} onClick={save} disabled={saving}
          sx={{ textTransform: 'none', bgcolor: '#1a1a2e', color: '#fff', '&:hover': { bgcolor: '#2a2a4e' } }}>
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))} variant="filled">{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
